'use client';

import React, { useEffect, useRef } from 'react';
import Script from 'next/script';
import { useRouter } from 'next/navigation';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@/redux/store';
import { fetchSocialLogin } from '@/redux/thunk/authThunk';
import { login } from '@/redux/slices/authSlice';
import { loginResponse } from '@/helpers/types/authTypes';
import { toast } from 'react-toastify';

// Type definitions for Google Identity Services
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
            ux_mode?: 'popup' | 'redirect';
          }) => void;
          renderButton: (
            parent: HTMLElement,
            options: {
              type?: 'standard' | 'icon';
              theme?: 'outline' | 'filled_blue' | 'filled_black';
              size?: 'large' | 'medium' | 'small';
              text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
              shape?: 'rectangular' | 'pill' | 'circle' | 'square';
              width?: string;
              locale?: string;
            },
          ) => void;
          prompt: () => void;
        };
      };
    };
  }
}

const GOOGLE_CLIENT_ID = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;

// Google Maps also defines window.google (without accounts), so check for GSI itself
const isGsiLoaded = () =>
  typeof window !== 'undefined' && !!window.google?.accounts?.id;

interface CredentialResponse {
  credential: string;
}

interface GoogleAuthComponentProps {
  className?: string;
  supplier_referral: string | null;
}

export default function GoogleAuthComponent({
  className = '',
  supplier_referral,
}: GoogleAuthComponentProps) {
  const router = useRouter();
  const dispatch = useDispatch<AppDispatch>();
  const googleWrapperRef = useRef<{ click: () => void } | null>(null);

  // Google login callback to handle JWT token
  const googleLoginCallback = async (response: CredentialResponse) => {
    try {
      console.log('Google response:', response);

      // Send credential to backend using existing thunk
      const data = await dispatch(
        fetchSocialLogin({
          provider: 'google',
          token: response.credential,
          supplier_referral: supplier_referral || null,
        }),
      ).unwrap();

      // Login user with response
      dispatch(login(data as loginResponse));

      router.replace('/');
    } catch (error: any) {
      console.error('Google sign-in error:', error);
      toast.error(error?.message || 'Sign-in failed');
    }
  };

  // Create fake Google wrapper for custom button
  const createFakeGoogleWrapper = () => {
    const googleLoginWrapper = document.createElement('div');
    // Hide the wrapper
    googleLoginWrapper.style.display = 'none';
    googleLoginWrapper.classList.add('custom-google-button');

    // Add the wrapper to body
    document.body.appendChild(googleLoginWrapper);

    // Use GSI javascript api to render the button inside our wrapper
    window.google?.accounts.id.renderButton(googleLoginWrapper, {
      type: 'icon',
      width: '200',
    });

    const googleLoginWrapperButton = googleLoginWrapper.querySelector(
      'div[role=button]',
    ) as HTMLElement;

    return {
      click: () => {
        googleLoginWrapperButton?.click();
      },
    };
  };

  // Handle custom button click
  const handleGoogleLogin = () => {
    if (googleWrapperRef.current) {
      // Use wrapper click to prevent Illegal invocation exception
      googleWrapperRef.current.click();
      // This will open GSI login and after success you will have
      // a response on googleLoginCallback method
    }
  };

  useEffect(() => {
    // Initialize Google Sign-In when the component mounts
    const initializeGoogleSignIn = () => {
      if (isGsiLoaded()) {
        // Initialize Google accounts
        window.google!.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID || '',
          ux_mode: 'popup',
          callback: googleLoginCallback,
        });

        // Create fake wrapper for custom button
        googleWrapperRef.current = createFakeGoogleWrapper();
      }
    };

    // Check if Google script is already loaded
    if (typeof window !== 'undefined') {
      if (isGsiLoaded()) {
        initializeGoogleSignIn();
      } else {
        // Wait for Google script to load
        const checkGoogleLoaded = setInterval(() => {
          if (isGsiLoaded()) {
            clearInterval(checkGoogleLoaded);
            initializeGoogleSignIn();
          }
        }, 100);

        // Cleanup interval on unmount
        return () => {
          clearInterval(checkGoogleLoaded);
          // Clean up the hidden wrapper if it exists
          const wrapper = document.querySelector('.custom-google-button');
          if (wrapper) {
            wrapper.remove();
          }
        };
      }
    }

    // Cleanup on unmount
    return () => {
      if (typeof window !== 'undefined') {
        const wrapper = document.querySelector('.custom-google-button');
        if (wrapper) {
          wrapper.remove();
        }
      }
    };
  }, []);

  return (
    <div
      className={`mt-1 w-full active:scale-95 transition-transform duration-100 ${className}`}
    >
      {GOOGLE_CLIENT_ID && (
        <Script
          src="https://accounts.google.com/gsi/client"
          strategy="afterInteractive"
        />
      )}
      <button
        onClick={handleGoogleLogin}
        className="w-full flex items-center justify-center gap-3 p-2 h-10 shadow-sm rounded-md bg-white hover:bg-gray-50 transition-colors border-1 border-primary"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            fill="#4285F4"
          />
          <path
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            fill="#34A853"
          />
          <path
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            fill="#FBBC05"
          />
          <path
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            fill="#EA4335"
          />
        </svg>
        <span className="text-sm font-medium text-gray-700">Google</span>
      </button>
    </div>
  );
}
